import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Svg, { Circle, G } from 'react-native-svg';
import { useTheme } from '../hooks/useTheme';

export interface CircularProgressProps {
  /** 0–100. Pass undefined for indeterminate spinning state. */
  progress?: number;
  size?: number;
  strokeWidth?: number;
}

export const CircularProgress: React.FC<CircularProgressProps> = ({
  progress,
  size = 180,
  strokeWidth = 10,
}) => {
  const { colors } = useTheme();
  const radius        = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;

  const isIndeterminate  = progress === undefined;
  const safeProgress     = Math.max(0, Math.min(100, progress ?? 0));
  const strokeDashoffset = isIndeterminate
    ? circumference * 0.75                             // 25 % arc for spinner
    : circumference - (circumference * safeProgress) / 100;

  const displayNum = Math.round(safeProgress);

  return (
    <View style={[styles.container, { width: size, height: size }]}>
      {/* SVG ring */}
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background track */}
        <Circle
          stroke={colors.pill as string}
          fill="none"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        {/* Progress arc */}
        <G origin={`${size / 2}, ${size / 2}`} rotation="-90">
          {isIndeterminate && typeof window !== 'undefined' && (
            <animateTransform
              attributeName="transform"
              type="rotate"
              from={`0 ${size / 2} ${size / 2}`}
              to={`360 ${size / 2} ${size / 2}`}
              dur="1s"
              repeatCount="indefinite"
            />
          )}
          <Circle
            stroke={colors.text as string}
            fill="none"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={strokeWidth}
            strokeDasharray={`${circumference} ${circumference}`}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
          />
        </G>
      </Svg>

      {/* Centre label */}
      <View style={styles.textContainer}>
        <View style={styles.numRow}>
          <Text style={[styles.numText, { color: colors.text }]}>
            {displayNum}
          </Text>
          <Text style={[styles.pctText, { color: colors.subtext }]}>%</Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems:     'center',
  },
  textContainer: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems:     'center',
  },
  numRow: {
    flexDirection: 'row',
    alignItems:    'flex-end',
  },
  numText: {
    fontSize:    52,
    fontWeight:  '300',
    lineHeight:  56,
    fontVariant: ['tabular-nums'],
  },
  pctText: {
    fontSize:    20,
    fontWeight:  '400',
    lineHeight:  36,  // aligns baseline with bottom of numText
    marginLeft:  2,
    fontVariant: ['tabular-nums'],
  },
});

